// The Brain stage's data layer. Every source is founder-scoped and already
// live in this codebase — nothing here is new backend surface.
//
//   agents     GET /api/organization/agents            lifecycle per agent
//   runs       GET /api/agent-runs                     current runs
//   notices    GET /api/organization/notifications     unacknowledged notices
//   health     GET /api/organization/health-topology   system failures
//
// A source that fails contributes nothing rather than zero. That distinction
// is the whole point: a Brain that renders "0 live agents" because a request
// 404'd is lying, while one that renders fewer facts is merely quieter.
import { useMemo } from "react";

import { useBrainResource } from "../../../components/brain/lib/hooks";
import { composeBrain } from "./brainState";

export default function useBrainLive() {
  const agents = useBrainResource("/organization/agents");
  const runs = useBrainResource("/agent-runs", { params: { limit: 50 } });
  const notices = useBrainResource("/organization/notifications");
  const health = useBrainResource("/organization/health-topology");

  const brain = useMemo(() => composeBrain({
    agents: agents.status === "ok" ? (agents.data && agents.data.agents) : undefined,
    runs: runs.status === "ok" ? (runs.data && runs.data.runs) : undefined,
    notices: notices.status === "ok" ? (notices.data && notices.data.notifications) : undefined,
    health: health.status === "ok" ? health.data : undefined,
    // The reasoning stream is read by the pilot surface, which owns the run
    // lifecycle. Binding it here would double-poll a founder-only endpoint for
    // a state that is currently unreachable in production; it is wired the day
    // a cycle actually runs.
    reasoning: undefined,
  }), [agents.status, agents.data, runs.status, runs.data,
       notices.status, notices.data, health.status, health.data]);

  // The stage renders as soon as the roster is known. The other three only
  // ever raise a state above healthy, so their absence cannot mislead.
  const status = agents.status === "ok" ? "ok" : agents.status;

  return { brain, status, sources: { agents, runs, notices, health } };
}
